/**
 * TASK-842 — a canvas link that could not be fully restored says so to the advisor.
 *
 * TASK-836 AC5 asked for three properties when a `?canvas=` param names a widget this build
 * does not have: no crash, nothing rendered blind, and **the advisor is told**. The first two
 * held. The third was a `console.warn`, so the person who followed a colleague's link saw a
 * canvas with one tile missing and nothing to say that anything was absent.
 *
 * It is a small gap, and it is the one that gets exercised in the real failure mode: a link
 * shared across a deploy that removed or renamed a widget.
 */
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { CanvasRestoreNotice as Notice } from '../../hooks/useCanvasUrlSync';

export function CanvasRestoreNoticeBanner({
  notice,
  onDismiss,
}: {
  notice: Notice | null;
  onDismiss: () => void;
}) {
  // TASK-820's rule: `t(key, text)`, so a locale with no entry renders the English rather
  // than the raw key. Hooks run before the early return — a conditional hook would be a
  // different bug.
  const { t } = useTranslation('dashboard');
  if (!notice) return null;

  // The TOTAL the link failed to open — named widgets this build lacks, plus entries that
  // could not be read. Counting only the named ones reported "One tile" for a link that lost
  // two, with the one name shown beside it implying that was the whole story.
  const count = notice.missing;
  //
  // The English is composed HERE rather than left to i18next's plural `defaultValue_one` /
  // `_other`, which only resolve when plural support is configured for the instance — and
  // when it is not, the fallback renders the literal `{{count}}` at the advisor. TASK-820's
  // rule is that a locale with no entry gets readable English; a template that leaks its own
  // placeholder is not that. Real locales still carry proper plural forms in their files.
  const englishFallback = count === 1
    ? 'One tile in this link could no longer be opened.'
    : `${count} tiles in this link could no longer be opened.`;
  const headline = count
    ? t('canvas.restore.partialNamed', { count, defaultValue: englishFallback })
    : t('canvas.restore.partial', 'Part of this link could not be read.');

  return (
    <div
      data-testid="canvas-restore-notice"
      role="status"
      className="mb-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
    >
      <div className="min-w-0 flex-1">
        <span className="font-medium">{headline}</span>
        {/* Only when something actually came back. A wholly unreadable param restores
            NOTHING and puts this banner on the empty-canvas branch — "Everything else was
            restored" over an empty canvas is a sentence asserting a restore that did not
            happen, which is the same defect shape as a tile stating a due date and denying
            it. */}
        {notice.restoredAny && (
          <> <span>{t('canvas.restore.rest', 'Everything else was restored.')}</span></>
        )}
        {notice.unknown.length > 0 && (
          // The names are supporting detail, not the message: an advisor does not think in
          // component names, but the colleague they ask about it does.
          <span data-testid="canvas-restore-notice-names" className="ml-1 opacity-70">
            ({notice.unknown.join(', ')})
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        data-testid="canvas-restore-notice-dismiss"
        aria-label={t('canvas.restore.dismiss', 'Dismiss')}
        className="shrink-0 rounded p-0.5 text-amber-700 transition-colors hover:bg-amber-100"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
